import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Alert, Button, App, Modal, Spin, QRCode, Row, Col } from 'antd';
import {
  DeviceType,
  deviceTypeLabels,
  getLicenseToolCode,
  getSmartCoreLicenseToolDiameter,
  isHourBasedLicense,
  LicenseDataItem,
  LicenseDataItemStore,
  LicenseSummaryStore,
} from '../../stores/licensing-store';
import moment from 'moment';
import { addPaddingToImage, getCurrentOrg, getDateFormat } from 'src/common/utils';
import SptLogo from 'src/assets/spt-logo.png';
import { UserSummaryStore } from 'src/stores/users-store';
import UsersSelect from 'src/common/components/business/users-select';
import { useIdentity } from 'src/core/services/authentication';

const ShowLicenseDialog: FC<{ onClose: () => void; licenseDataId: string }> = ({ onClose, licenseDataId }) => {
  const { t } = useTranslation();
  const licenseStore = useMemo(() => container.get(LicenseSummaryStore), []);
  const licenseState = licenseStore.state;
  const licenseDataStore = container.get(LicenseDataItemStore);
  const licenseDataState = licenseDataStore.state;
  const [footer, setFooter] = useState<any>([]);
  const { message } = App.useApp();
  const licenseTextRef = useRef<string>('');
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const shareUsers = useRef<{ label: string; value: string }[]>([]);
  const usersStore = container.get(UserSummaryStore);
  const [errorMessage, setErrorMessage] = useState<string>();
  const identity = useIdentity();
  const currentOrg = getCurrentOrg(identity);

  const getWorkingHoursForEmail = (unlimited: boolean, workingHours: number | undefined) => {
    if (!unlimited) return workingHours;
    if (workingHours !== undefined) return -1;
  };

  const shareLicense = async () => {
    if (shareUsers.current.length === 0) {
      setErrorMessage(t('NoUsersSelectedError'));
      return;
    }
    setErrorMessage(undefined);
    const canvas = document.getElementById('license-qr-code')?.querySelector<HTMLCanvasElement>('canvas');
    if (canvas) {
      const image = canvas.toDataURL();
      const imageWithPadding = await addPaddingToImage(image);
      const response = await usersStore.shareLicenseData({
        licenseData: licenseState.item.value,
        unlimited: licenseDataState.item.value.unlimited,
        deviceType: licenseDataState.item.value.deviceType,
        toolCode: licenseDataState.item.value.toolCode,
        fromDate: licenseDataState.item.value.unlimited ? undefined : licenseDataState.item.value.fromDate,
        untilDate: licenseDataState.item.value.unlimited ? undefined : licenseDataState.item.value.untilDate,
        workinHours: getWorkingHoursForEmail(licenseDataState.item.value.unlimited, licenseDataState.item.value.workingHours),
        licenseQR: imageWithPadding.url,
        users: shareUsers.current.map((v) => v.value),
      });
      if (response?.isSuccess) {
        setErrorMessage(undefined);
        setSendEmailModalVisible(false);
        shareUsers.current = [];
      } else {
        setErrorMessage(t('UnexpectedError'));
      }
    }
  };
  useEffect(
    () => {
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const generateLicenseText = () => {
    var deviceType = `${t('DeviceType')}: ${deviceTypeLabels[licenseDataState.item.value.deviceType]}`;
    var toolCode = `${t('ToolCode')}: ${getLicenseToolCode(licenseDataState.item.value as LicenseDataItem)}`;
    var fromDate = `${t('FromDate')}: ${
      licenseDataState.item.value.unlimited
        ? '-'
        : moment((licenseDataState.item.value as LicenseDataItem).fromDate)
            .utc()
            .format(getDateFormat())
    }`;
    var untilDate = `${t('UntilDate')}: ${
      licenseDataState.item.value.unlimited
        ? '-'
        : moment((licenseDataState.item.value as LicenseDataItem).untilDate)
            .utc()
            .format(getDateFormat())
    }`;
    var license = `${t('License')}: ${
      licenseDataState.item.value.needsSignOff && !licenseDataState.item.value.signedOffByUserName ? t('ApprovalPending') : licenseState.item.value
    }`;
    var workingHours = `${t('WorkingHours')}: ${
      licenseDataState.item.value.unlimited ? '-' : (licenseDataState.item.value as LicenseDataItem).workingHours
    }`;
    var toolDiameter =
      licenseDataState.item.value.deviceType === DeviceType.SCTD
        ? `${t('ToolDiameter')}: ${getSmartCoreLicenseToolDiameter(licenseDataState.item.value as LicenseDataItem)}`
        : '';
    var recurrence = `${t('Recurrence')}: ${licenseDataState.item.value.isRecurrent ? t('Activated') : t('Deactivated')}`;
    var toCopy = '';
    if (isHourBasedLicense(licenseDataState.item.value.deviceType)) {
      toCopy = deviceType
        .concat('\n')
        .concat(toolCode)
        .concat('\n')
        .concat(workingHours)
        .concat('\n')
        .concat(recurrence)
        .concat('\n')
        .concat(license);
    } else {
      if (licenseDataState.item.value.deviceType === DeviceType.SCTD) {
        toCopy = deviceType
          .concat('\n')
          .concat(toolDiameter)
          .concat('\n')
          .concat(toolCode)
          .concat('\n')
          .concat(fromDate)
          .concat('\n')
          .concat(untilDate)
          .concat('\n')
          .concat(recurrence)
          .concat('\n')
          .concat(license);
      } else {
        toCopy = deviceType
          .concat('\n')
          .concat(toolCode)
          .concat('\n')
          .concat(fromDate)
          .concat('\n')
          .concat(recurrence)
          .concat('\n')
          .concat(untilDate)
          .concat('\n')
          .concat(license);
      }
    }
    licenseTextRef.current = toCopy;
  };

  const load = async () => {
    await licenseDataStore.load(licenseDataId);
    await licenseStore.load(licenseDataId);
    generateLicenseText();
    if (!licenseDataState.item.value.needsSignOff || licenseDataState.item.value.signedOffByUserName) {
      setFooter([
        <Row gutter={[0, 10]}>
          <Col>
            <Button key="copy-license" type="primary" onClick={() => onCopyLicenseClick()}>
              {t('CopyLicense')}
            </Button>
          </Col>
          <Col>
            <Button key="copy-all" type="primary" onClick={() => onCopyAllClick()}>
              {t('CopyAll')}
            </Button>
          </Col>
          <Col>
            <Button
              key="download-qr"
              type="primary"
              onClick={() => {
                shareUsers.current = [];
                setErrorMessage(undefined);
                setSendEmailModalVisible(true);
              }}
            >
              {t('Share')}
            </Button>
          </Col>
        </Row>,
      ]);
    }
  };

  const onCopyLicenseClick = () => {
    navigator.clipboard.writeText(licenseState.item.value);
    message.success(t('LicenseCopied'));
  };
  const onCopyAllClick = () => {
    navigator.clipboard.writeText(licenseTextRef.current);
    message.success(t('LicenseCopied'));
  };
  return (
    <Modal
      className="figma-modal license-modal"
      title={<h3 style={{ margin: '12px 16px' }}>{t('License')}</h3>}
      open={true}
      onOk={() => {
        navigator.clipboard.writeText(licenseState.item.value);
      }}
      okButtonProps={{ style: { width: '45%', float: 'right' } }}
      onCancel={onClose}
      cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
      okText={t('Copy')}
      cancelText={t('Cancel')}
      closable={true}
      footer={footer}
    >
      {licenseState.errorMessage.value && licenseDataState.errorMessage.value && <Alert type="error" message={t('ErrorLoadingLicense')} />}
      {
        <Spin spinning={licenseState.isBusy.get() || licenseDataState.isBusy.get()}>
          {licenseState.item.value && licenseDataState.item.value && (
            <>
              <label>{`${t('DeviceType')}: ${deviceTypeLabels[licenseDataState.item.value.deviceType]}`}</label>
              <br />
              {licenseDataState.item.value.deviceType === DeviceType.SCTD && (
                <>
                  <label>{`${t('ToolDiameter')}: ${getSmartCoreLicenseToolDiameter(licenseDataState.item.value as LicenseDataItem)}`}</label>
                  <br />
                </>
              )}
              <label>{`${t('ToolCode')}: ${getLicenseToolCode(licenseDataState.item.value as LicenseDataItem)}`}</label>
              <br />
              {isHourBasedLicense(licenseDataState.item.value.deviceType) && (
                <>
                  <label>{`${t('WorkingHours')}: ${licenseDataState.item.value.unlimited ? '-' : licenseDataState.item.value.workingHours}`}</label>
                  <br />
                </>
              )}
              {!isHourBasedLicense(licenseDataState.item.value.deviceType) && (
                <>
                  <label>{`${t('FromDate')}: ${
                    licenseDataState.item.value.unlimited ? '-' : moment(licenseDataState.item.value.fromDate).utc().format(getDateFormat())
                  }`}</label>
                  <br />
                  <label>{`${t('UntilDate')}: ${
                    licenseDataState.item.value.unlimited ? '-' : moment(licenseDataState.item.value.untilDate).utc().format(getDateFormat())
                  }`}</label>
                  <br />
                </>
              )}
              <label>{`${t('Recurrence')}: ${licenseDataState.item.value.isRecurrent ? t('Activated') : t('Deactivated')}`}</label>
              <br />
              <label>{`${t('License')}: ${
                licenseDataState.item.value.needsSignOff && !licenseDataState.item.value.signedOffByUserName
                  ? t('ApprovalPending')
                  : licenseState.item.value
              }`}</label>
              <br />
              {!(licenseDataState.item.value.needsSignOff && !licenseDataState.item.value.signedOffByUserName) && (
                <div id="license-qr-code" style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <QRCode icon={SptLogo} color="black" bgColor="white" value={licenseTextRef.current} size={300} />
                </div>
              )}

              {sendEmailModalVisible && (
                <Modal
                  maskClosable={false}
                  title={t('SelectUsersToShare')}
                  open
                  onCancel={() => setSendEmailModalVisible(false)}
                  onOk={async () => {
                    await shareLicense();
                  }}
                >
                  {errorMessage && <Alert type="error" message={errorMessage} />}
                  <UsersSelect
                    placeholder={t('SelectOrEnterEmailAddress')}
                    allowInviteUser
                    organizationFilter={currentOrg.id}
                    onChange={(users) => {
                      shareUsers.current = users;
                    }}
                  />
                </Modal>
              )}
            </>
          )}
        </Spin>
      }
    </Modal>
  );
};

export default ShowLicenseDialog;
